import DeviceSettings from '@/features/twilio/components/DeviceSettings'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { ComponentProps } from 'react'
import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneXmark,
} from '@fortawesome/pro-solid-svg-icons'
import IconSquareButton from '@/shared/components/IconSquareButton'
import Link from 'next/link'
import Spinner from '@/shared/components/Spinner'
import TextButton from '@/shared/components/buttons/TextButton'

type Metadata = {
  title: string
  href: string
}

export type CallScreenStatus =
  | {
      status: 'IN_PROGRESS'
      elapsedMs: number
      metadata: Metadata[]
    }
  | {
      status: 'IDLE'
    }
  | { status: 'LOADING' }

type Props = {
  status: CallScreenStatus
  closeable: boolean
  onClose: () => void
  isMuted: boolean
  mute: () => void
  unmute: () => void
  endCall: () => void
} & ComponentProps<typeof DeviceSettings>

export default function CallScreen({
  status,
  onClose,
  isMuted,
  mute,
  unmute,
  endCall,
}: Props) {
  let label = ''
  if (status.status === 'IN_PROGRESS') {
    const minutes = Math.round(status.elapsedMs / (1000 * 60))
    const seconds = Math.round((status.elapsedMs / 1000) % 60)
    const minutesPadded = String(minutes).padStart(2, '0')
    const secondsPadded = String(seconds).padStart(2, '0')
    label = `${minutesPadded}:${secondsPadded}`
  }

  return (
    <div className="fixed top-0 z-50 w-[100vw] cursor-auto rounded-sm border-b border-b-slate-400 bg-white p-4">
      {status.status === 'IDLE' && (
        <div className="flex w-full items-center justify-between">
          <p className="align-middle leading-6 text-gray-600">
            You{"'"}re not in a call
          </p>
          <TextButton
            title="Close"
            color="blue"
            onClick={onClose}
            className="px-8"
          />
        </div>
      )}
      {status.status === 'LOADING' && (
        <div className="flex w-full items-center justify-center gap-x-4">
          <Spinner size={6} />
          <p className="leading-6 text-gray-700">Establishing connection....</p>
        </div>
      )}
      {status.status === 'IN_PROGRESS' && (
        <div className="flex justify-between">
          <div className="flex gap-x-2.5">
            <IconSquareButton
              icon={
                <FontAwesomeIcon
                  icon={isMuted ? faMicrophoneSlash : faMicrophone}
                  size="xl"
                />
              }
              color="gray"
              appearance="ring"
              className="shrink-0"
              onClick={isMuted ? unmute : mute}
            />
            <IconSquareButton
              icon={<FontAwesomeIcon icon={faPhoneXmark} size="xl" />}
              color="red"
              appearance="ring"
              className="shrink-0"
              onClick={() => {
                endCall()
                onClose()
              }}
            />
            <div className="flex flex-col">
              <p className="truncate font-semibold text-gray-900">
                Call in progress
              </p>
              <p className="text-gray-500">{label}</p>
            </div>
          </div>
          <div className="flex flex-col items-end">
            {status.metadata.map((md) => (
              <Link
                key={md.href}
                href={md.href}
                className="font-normal text-blue-500 hover:text-blue-600"
                target="_blank"
              >
                {md.title}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
